import Button from 'components/Button';
import Field from 'components/Field';
import Loading from 'components/Loading';
import Modal from 'components/Modal';
import PropTypes from 'prop-types';
import React from 'react';
import {
  getCertForServer,
  getCertList,
  importCadesplugin_api,
  signCreate,
} from 'services/crypto/crypto';
import Select from '../../../../../components/Select/index';
import './ESAuth.scss';
import { getUuid } from '../../../services/API';
import { get } from 'lodash';
import { VALIDATE_ERRORS } from '../../../../../constants/crypto';
import SignatureTypes from '../../../../../constants/SignatureTypes';
import SignService from '../../../../../services/sign/index';

export const ERROR_MESSAGES = {
  BAD_CERTIFICATE: VALIDATE_ERRORS.CONTROL_SUM,
  CERTIFICATE_EXPIRED: VALIDATE_ERRORS.TO_DATE,
  CERTIFICATE_NOT_VALID_YET: VALIDATE_ERRORS.FROM_DATE,
  OGRN_FOUND: VALIDATE_ERRORS.HAS_OGRN,
  INN_NEEDED: VALIDATE_ERRORS.INN,
  CERTIFICATE_IS_NOT_TRUSTED: VALIDATE_ERRORS.CERTIFICATE_CHAIN,
  BAD_KEY_FORMAT: VALIDATE_ERRORS.IS_GOST,
  BAD_POLICIES: VALIDATE_ERRORS.BAD_POLICIES,
  CERTIFICATE_REVOKED: VALIDATE_ERRORS.IS_REVOKED[SignatureTypes.QES],
  OTHER_ERROR: 'Произошла неизвестная ошибка. Попробуйте позже.',
  BAD_CONNECTION:
    'Соединение не установлено. Пожалуйста, проверьте, выполнены ли технические условия, изложенные на ' +
    'этой странице',
  PPLK_ERROR: 'Неверный ИНН/Пароль',
  PPLK_WAIT: 'Ваш личный кабинет обновляется. Это может занять некоторое время',
};

export default class ESAuth extends React.Component {
  static propTypes = {
    showModal: PropTypes.bool.isRequired,
    onHideModal: PropTypes.func.isRequired,
    onAuthorize: PropTypes.func.isRequired,
    checkSuccess: PropTypes.bool.isRequired,
    onStartWorking: PropTypes.func.isRequired,
    error: PropTypes.string.isRequired,
    errorId: PropTypes.string,
    loading: PropTypes.bool.isRequired,
  };

  state = {
    loading: false,
    certs: [],
    certificateList: [],
    email: '',
    selectedCert: '',
    CSPInstalled: null,
    emailValidationMessage: null,
    certError: false,
    validationError: '',
  };

  componentDidUpdate(prevProps) {
    if (this.props.showModal && !prevProps.showModal) {
      this.initCadesPlugin();
    }
  }

  initCadesPlugin = async () => {
    importCadesplugin_api()
      .then(() => {
        this.setState({
          CSPInstalled: true,
        });
        this.getCerts();
      })
      .catch(() =>
        this.setState({
          CSPInstalled: false,
        }),
      );
  };

  getCerts() {
    this.setState({
      loading: true,
      certs: [],
      selectedCert: '',
    });

    getCertList()
      .then((_certs) => {
        const certs = _certs.map((c) => ({
          value: c.value,
          label: `${c.subject}; Выдан ${c.ValidFromDate.format('DD.MM.YYYY HH:mm:ss')}`,
        }));

        this.setState({
          loading: false,
          certs,
          certificateList: _certs,
          selectedCert: certs.length === 1 ? certs[0].value : '',
        });
      })
      .catch(() =>
        this.setState({
          certError: true,
          loading: false,
        }),
      );
  }

  async getUuid(inn) {
    const response = await getUuid(inn);
    if (!response.isOk) {
      const error = get(response, 'response.error.response.data.error_description');
      throw ERROR_MESSAGES[error] || ERROR_MESSAGES.OTHER_ERROR;
    }

    return get(response, 'data.code');
  }

  async checkCert() {
    this.setState({ loading: true });
    try {
      const { selectedCert, certificateList } = this.state;
      const cert = certificateList.find((certificate) => certificate.value === selectedCert);
      const certHash = await getCertForServer(selectedCert);
      const subject = (cert || {}).subjectObj || {};
      const inn = subject.INN;

      SignService.validateCertificate(cert, inn);

      const uuid = await this.getUuid(inn);
      const stringForSign = `${uuid}_${inn}`;

      const sign = await signCreate(selectedCert, stringForSign);
      this.setState({ loading: false });
      this.props.onAuthorize({ sign, certHash });
    } catch (error) {
      this.setState({
        validationError: error.friendlyErrorMessage ? error.friendlyErrorMessage : error,
        loading: false,
      });
    }
  }

  getErrorMessage() {
    return this.props.error || this.state.validationError || ERROR_MESSAGES.OTHER_ERROR;
  }

  getModalBody() {
    const buttonsIsDisabled = !this.state.selectedCert || this.state.loading;

    return (
      <div>
        <h2>Вход в Личный кабинет</h2>
        <p>
          Доступ к сервису Личный кабинет налогоплательщика для физических лиц возможен с помощью ЭП
          без посещения налогового органа.
        </p>
        <p>
          Квалифицированный сертификат ключа проверки ЭП налогоплательщик может получить в{' '}
          <a
            target="_blank"
            href="https://digital.gov.ru/ru/activity/govservices/certification_authority/"
          >
            Удостоверяющем центре, аккредитованном Минцифры России.
          </a>
        </p>
        <p>
          Для доступа к сервису налогоплательщик, используя ЭП, заполняет в электронном виде
          заявление на подключение со стартовой страницы сервиса. При этом необходимые для
          идентификации пользователя реквизиты считываются с носителя ЭП и автоматически
          подгружаются в форму заявления, дополнительные необязательные реквизиты «Номер телефона»,
          «Электронная почта» вводятся вручную.
        </p>
        <p>
          Для получения доступа к сервису Личный кабинет налогоплательщика для физических лиц с
          помощью ЭП необходимо обеспечить выполнение ряда технических условий:
        </p>
        <ol className="list-number">
          <li>
            Операционная система Microsoft Windows версий XP SP3 и выше (например, Windows 7).
          </li>
          <li>
            Веб-браузер одной из поддерживаемых версий:
            <ul>
              <li>Microsoft Edge</li>
              <li>Google Chrome 48+</li>
              <li>Mozilla Firefox 45+</li>
              <li>Yandex Browser 14+</li>
              <li>Safari 9.0+</li>
            </ul>
          </li>
          <li>
            Крипто ПРО CSP 3.6 или выше с действующей лицензией (скачать с официального сайта
            компании "Крипто-Про", заполнив форму регистрации);
          </li>
          <li>Драйверы ключевых носителей (eToken, RuToken и т.п.).</li>
        </ol>
        <p className="bold">
          Установить корневой сертификат{' '}
          <a href="http://uc.nalog.ru/crt/CA_FNS_Russia_2015.crt">УЦ ФНС России</a>
        </p>
        <div className="cert-container">
          <span className="bold">Выберите сертификат</span>
          {this.state.loading ? (
            <Loading />
          ) : this.state.certError ? (
            <p className="red bold">Ошибка получения сертификатов</p>
          ) : (
            <Select
              options={this.state.certs}
              placeholder="Выберите сертификат"
              style={{ width: '500px' }}
              value={this.state.selectedCert}
              onChange={(c) => this.setState({ selectedCert: c.value })}
            />
          )}
        </div>

        {this.props.checkSuccess ? (
          <div className="popup_message">
            Проверка сертификата ЭП завершилась успешно. Вы можете начать работу в Личном кабинете
          </div>
        ) : null}

        {this.props.error || this.state.validationError ? (
          <div className="popup_message">
            <span className="bold red">Ошибка. </span>
            {this.getErrorMessage()}
          </div>
        ) : null}

        <div className="popup_bottom">
          <div className="popup_left">
            <Button
              buttonStyle={{ position: 'relative' }}
              isDisabled={buttonsIsDisabled}
              onClick={() => this.checkCert()}
            >
              {this.props.loading ? <Loading isLight={true} /> : 'Проверка'}
            </Button>
          </div>
          <div className="popup_right">
            <Button
              isDisabled={buttonsIsDisabled || !this.props.checkSuccess}
              color="orange"
              onClick={this.props.onStartWorking}
            >
              Начать работу
            </Button>
          </div>
        </div>
      </div>
    );
  }

  onChangeEmail = (event) => {
    this.setState({
      email: event.target.value,
      emailValidationMessage: null,
    });
  };

  validateEmail = (email = '') => {
    const isValid = email.match(/^[a-zа-яё0-9._%+-]+@[a-zа-яё0-9.-]+\.[a-zа-яё]{2,6}$/i);
    if (!isValid) {
      this.setState({
        emailValidationMessage: 'Введенный адрес электронной почты не соответствует формату',
      });
    }

    return isValid;
  };

  saveEmail = (e) => {
    e.preventDefault();
    const { email, selectedCert } = this.state;
    const isValidEmail = this.validateEmail(email);

    if (email && isValidEmail) {
      this.props.onAuthorize(selectedCert, email);
    }
  };

  getModalContent() {
    switch (this.state.CSPInstalled) {
      case null:
        return <Loading />;
      case true:
        return this.props.errorId === 'EMAIL_NEEDED'
          ? this.renderEmailBlock()
          : this.getModalBody();
      case false:
        return this.renderSCPNotInstalledError();
    }
  }
  renderSCPNotInstalledError() {
    return (
      <div>
        <h2>Ошибка</h2>
        <p>
          На Вашем компьютере не установлен плагин для работы с сертификатом квалифицированной ЭП (
          <a target="_blank" href="https://www.cryptopro.ru/products/cades/plugin/get_2_0">
            скачать
          </a>
          )
        </p>
      </div>
    );
  }
  renderEmailBlock() {
    const validationMessage = this.state.emailValidationMessage;
    const validationObject = validationMessage
      ? { validationMessage, isValid: !validationMessage }
      : {};

    return (
      <div>
        <p className="page_margin30">Пожалуйста, укажите электронную почту.</p>
        <form className="form">
          <Field
            label="Адрес электронной почты"
            type="email"
            value={this.state.email}
            isValid={!validationMessage}
            {...validationObject}
            onChange={this.onChangeEmail}
          />
          <div className="cRow page_margin20">
            <div className="cCol">
              <Button
                onClick={this.saveEmail}
                type="submit"
                color="orange"
                buttonStyle={{ position: 'relative', minWidth: '222px' }}
              >
                {this.props.loading ? <Loading isLight={true} /> : 'Подтвердить'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    );
  }

  render() {
    const { showModal, onHideModal } = this.props;

    return (
      <Modal type="large" onClose={onHideModal} showModal={showModal}>
        {this.getModalContent()}
      </Modal>
    );
  }
}
